import { Layers, AlertTriangle } from 'lucide-react';
import type { POLineGrainSite } from '../../../lib/api/po-line-dashboard';

interface GrainBreakdownNoteProps {
  sites: POLineGrainSite[];
  /** Currency every group figure is held in. */
  currency: string;
  missingRates?: string[];
  formatCurrency: (value: number, currency?: string) => string;
}

const GRAIN_LABEL: Record<POLineGrainSite['grain'], string> = {
  schedules: 'schedule lines',
  po_lines: 'PO lines',
};

/**
 * Mixed-grain group roll-up: names each site, the grain it is judged on and
 * its own arrears stock, so the group total can be read back to its parts.
 * Schedule sites' drill-through rows live on the Scheduled tab.
 *
 * @param sites - the payload's ``grain_breakdown``
 * @param currency - the group currency the figures are converted into
 * @param missingRates - site currencies left out for want of a rate
 */
export function GrainBreakdownNote({ sites, currency, missingRates, formatCurrency }: GrainBreakdownNoteProps) {
  const hasSchedule = sites.some((s) => s.grain === 'schedules');
  return (
    <div className="mt-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900" data-testid="grain-breakdown-note">
      <div className="flex items-start gap-1.5">
        <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="space-y-0.5">
          <p className="font-medium">Judged site by site, converted to {currency}</p>
          <ul className="space-y-0.5">
            {sites.map((s) => (
              <li key={s.customer_id}>
                <span className="font-medium">{s.name}</span> — on {GRAIN_LABEL[s.grain]}
                {s.currency !== currency && <> (held in {s.currency})</>}
                {s.arrears_value != null && s.arrears_lines != null && (
                  <>: arrears {formatCurrency(s.arrears_value, currency)} across {s.arrears_lines.toLocaleString()} {s.grain === 'schedules' ? 'schedule lines' : 'lines'}</>
                )}
              </li>
            ))}
          </ul>
          {hasSchedule && (
            <p className="text-sky-800">Drill-downs list PO lines; schedule-line rows are on the Scheduled tab.</p>
          )}
        </div>
      </div>
      {missingRates && missingRates.length > 0 && (
        <p className="mt-1 flex items-center gap-1.5 text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5" />
          No exchange rate from {missingRates.join(', ')} to {currency}: those sites&apos; money is left out of the totals.
        </p>
      )}
    </div>
  );
}

export default GrainBreakdownNote;
